"use client";

// =========================================================================
// The contact source behind ClientSelectWithModal: paginated browse when
// nothing is typed, FTS results once something is. Split out of
// ClientSelectWithModal.tsx (module-size).
// =========================================================================

import { useEffect, useMemo, useState } from "react";
import type { Client } from "@/lib/types/database";
import { useContactsPaginated } from "@/features/contacts/hooks";
import { useAccumulatedList } from "../shared/useAccumulatedList";
import { useFTSWithDebouncedSearch } from "../shared/useFTSWithDebouncedSearch";

const PAGE_LIMIT = 30;
const FIRST_PAGE = 1;

export function useClientSelectContacts() {
  const { searchValue, setSearchValue, hasSearchTerm, ftsResults, ftsLoading } =
    useFTSWithDebouncedSearch<Client>({ entityType: "contacts" });

  const [page, setPage] = useState(FIRST_PAGE);

  // Browse pagination only. Narrowing is FTS's job (see `searchedClients`), so
  // no name/company/email filter is passed here — the previous
  // `hasSearchTerm ? undefined : undefined` said the same thing by accident.
  const {
    data: contactsPage,
    isLoading: paginationFetching,
    isFetching: paginationBackgroundFetching,
  } = useContactsPaginated({
    page,
    limit: PAGE_LIMIT,
    isActive: true,
    nameSearch: undefined,
    companySearch: undefined,
    emailSearch: undefined,
  });

  useEffect(() => {
    if (!hasSearchTerm) setPage(FIRST_PAGE);
  }, [hasSearchTerm]);

  const pageItems = useMemo<Client[]>(
    () => (contactsPage?.data as Client[]) ?? [],
    [contactsPage],
  );

  const accumulatedClients = useAccumulatedList<Client>({
    page: contactsPage?.page ?? page,
    firstPage: FIRST_PAGE,
    items: pageItems,
    idOf: (c) => String(c.id),
    paused: hasSearchTerm,
  });

  const searchedClients = useMemo<Client[]>(() => {
    if (hasSearchTerm) {
      return ftsResults.filter((c) => c?.is_active !== false);
    }
    return accumulatedClients.length > 0 ? accumulatedClients : pageItems;
  }, [hasSearchTerm, ftsResults, accumulatedClients, pageItems]);

  return {
    searchValue,
    setSearchValue,
    hasSearchTerm,
    searchedClients,
    isFetching: hasSearchTerm ? ftsLoading : paginationFetching,
    isBackgroundFetching: Boolean(paginationBackgroundFetching),
    page,
    totalPages: contactsPage?.totalPages ?? 1,
    firstPage: FIRST_PAGE,
    loadMore: () => setPage((p) => p + 1),
  };
}
